import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useBranding } from "@/context/BrandingContext";

const AdminBranding = () => {
  const { refresh } = useBranding();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/branding").then(r => setData(r.data)); }, []);

  const save = async () => {
    try {
      await api.patch("/admin/branding", data);
      toast.success("Branding updated");
      refresh();
    } catch { toast.error("Save failed"); }
  };

  if (!data) return <p className="text-cream/75">Loading…</p>;

  const set = (k) => (e) => setData({ ...data, [k]: e.target.value });

  return (
    <div data-testid="admin-branding-page">
      <p className="eyebrow mb-1">Identity</p>
      <h1 className="font-hero text-3xl mb-6">Branding</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl">
        <section className="bg-white rounded-2xl border border-border p-6 space-y-4">
          <p className="eyebrow">Identity</p>
          <div><label className="eyebrow">Brand name</label><Input value={data.brand_name} onChange={set("brand_name")} /></div>
          <div><label className="eyebrow">Tagline</label><Input value={data.tagline} onChange={set("tagline")} /></div>
          <div><label className="eyebrow">Logo URL</label><Input value={data.logo_url || ""} onChange={set("logo_url")} placeholder="https://…" /></div>
          <div><label className="eyebrow">Favicon URL</label><Input value={data.favicon_url || ""} onChange={set("favicon_url")} /></div>
        </section>

        <section className="bg-white rounded-2xl border border-border p-6 space-y-4">
          <p className="eyebrow">Story</p>
          <div><label className="eyebrow">Short tagline</label><Input value={data.about_short || ""} onChange={set("about_short")} /></div>
          <div><label className="eyebrow">About paragraph</label><Textarea rows={5} value={data.about_long || ""} onChange={set("about_long")} /></div>
        </section>

        <section className="bg-white rounded-2xl border border-border p-6 space-y-4 lg:col-span-2" data-testid="branding-socials">
          <p className="eyebrow">Social links <span className="normal-case tracking-normal text-charcoal/60">— shown in the footer; leave blank to hide that icon</span></p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><label className="eyebrow">Instagram URL</label><Input value={data.instagram_url || ""} onChange={set("instagram_url")} placeholder="https://instagram.com/your-handle" /></div>
            <div><label className="eyebrow">Facebook URL</label><Input value={data.facebook_url || ""} onChange={set("facebook_url")} placeholder="https://facebook.com/your-page" /></div>
            <div><label className="eyebrow">Twitter / X URL</label><Input value={data.twitter_url || ""} onChange={set("twitter_url")} placeholder="https://twitter.com/your-handle" /></div>
            <div><label className="eyebrow">YouTube URL</label><Input value={data.youtube_url || ""} onChange={set("youtube_url")} /></div>
            <div><label className="eyebrow">Pinterest URL</label><Input value={data.pinterest_url || ""} onChange={set("pinterest_url")} /></div>
            <div><label className="eyebrow">LinkedIn URL</label><Input value={data.linkedin_url || ""} onChange={set("linkedin_url")} /></div>
            <div><label className="eyebrow">WhatsApp URL <span className="normal-case tracking-normal text-charcoal/60">(wa.me/91…)</span></label><Input value={data.whatsapp_url || ""} onChange={set("whatsapp_url")} placeholder="https://wa.me/919999999999" /></div>
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-border p-6 space-y-5 lg:col-span-2" data-testid="branding-footer">
          <p className="eyebrow">Footer content</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><label className="eyebrow">Newsletter heading <span className="normal-case tracking-normal text-charcoal/60">(last word becomes italic gold)</span></label><Input value={data.footer_newsletter_heading || ""} onChange={set("footer_newsletter_heading")} placeholder="Stay in the glow." /></div>
            <div><label className="eyebrow">Bottom-right tagline</label><Input value={data.footer_tagline || ""} onChange={set("footer_tagline")} placeholder="Handcrafted in India · Shipping worldwide" /></div>
            <div className="md:col-span-2"><label className="eyebrow">Newsletter blurb</label><Textarea rows={2} value={data.footer_newsletter_body || ""} onChange={set("footer_newsletter_body")} /></div>
            <div className="md:col-span-2"><label className="eyebrow">Bottom-left copyright override <span className="normal-case tracking-normal text-charcoal/60">(leave blank to auto-generate)</span></label><Input value={data.footer_copyright || ""} onChange={set("footer_copyright")} placeholder={`© ${new Date().getFullYear()} ${data.brand_name || "rieave"} · ${data.tagline || ""}`} /></div>
          </div>

          {/* Footer columns editor */}
          <div className="pt-4 border-t border-border">
            <div className="flex items-center justify-between mb-3">
              <p className="eyebrow">Link columns <span className="normal-case tracking-normal text-charcoal/60">(leave empty to use defaults: Shop + Care)</span></p>
              <button
                type="button"
                onClick={() => setData({ ...data, footer_columns: [...(data.footer_columns || []), { title: "New column", links: [{ label: "Link", url: "/" }] }] })}
                className="text-sm text-rose hover:text-rose/80"
                data-testid="footer-add-column"
              >+ Add column</button>
            </div>
            {(data.footer_columns || []).length === 0 && (
              <p className="text-sm text-charcoal/60 italic">No custom columns — using defaults (Shop + Care).</p>
            )}
            {(data.footer_columns || []).map((col, ci) => (
              <div key={ci} className="rounded-xl border border-border bg-blush/15 p-4 mb-3" data-testid={`footer-col-${ci}`}>
                <div className="flex gap-2 mb-3">
                  <Input
                    placeholder="Column title (e.g. Shop)"
                    value={col.title || ""}
                    onChange={(e) => {
                      const arr = [...data.footer_columns];
                      arr[ci] = { ...arr[ci], title: e.target.value };
                      setData({ ...data, footer_columns: arr });
                    }}
                    data-testid={`footer-col-title-${ci}`}
                  />
                  <button
                    type="button"
                    onClick={() => setData({ ...data, footer_columns: data.footer_columns.filter((_, i) => i !== ci) })}
                    className="px-3 rounded-lg border border-border hover:border-rose hover:text-rose text-charcoal/70"
                    data-testid={`footer-col-remove-${ci}`}
                  >Remove column</button>
                </div>
                <div className="space-y-2">
                  {(col.links || []).map((lnk, li) => (
                    <div key={li} className="flex gap-2" data-testid={`footer-col-${ci}-link-${li}`}>
                      <Input
                        placeholder="Label"
                        value={lnk.label || ""}
                        onChange={(e) => {
                          const arr = [...data.footer_columns];
                          arr[ci].links[li] = { ...arr[ci].links[li], label: e.target.value };
                          setData({ ...data, footer_columns: arr });
                        }}
                      />
                      <Input
                        placeholder="URL or path (e.g. /shop or https://…)"
                        value={lnk.url || ""}
                        onChange={(e) => {
                          const arr = [...data.footer_columns];
                          arr[ci].links[li] = { ...arr[ci].links[li], url: e.target.value };
                          setData({ ...data, footer_columns: arr });
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const arr = [...data.footer_columns];
                          arr[ci].links = arr[ci].links.filter((_, i) => i !== li);
                          setData({ ...data, footer_columns: arr });
                        }}
                        className="px-2 rounded-lg border border-border text-charcoal/60 hover:text-rose hover:border-rose"
                        aria-label="remove link"
                      >×</button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      const arr = [...data.footer_columns];
                      arr[ci].links = [...(arr[ci].links || []), { label: "", url: "" }];
                      setData({ ...data, footer_columns: arr });
                    }}
                    className="text-xs text-rose hover:text-rose/80"
                  >+ Add link</button>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-border p-6 space-y-4">
          <p className="eyebrow">Contact</p>
          <div><label className="eyebrow">Support email</label><Input value={data.support_email || ""} onChange={set("support_email")} /></div>
          <div><label className="eyebrow">Support phone</label><Input value={data.support_phone || ""} onChange={set("support_phone")} /></div>
        </section>

        <section className="bg-white rounded-2xl border border-border p-6 space-y-4 lg:col-span-2" data-testid="branding-shipping">
          <p className="eyebrow">Shipping fee</p>
          <p className="text-xs text-charcoal/60 -mt-2">Charged at checkout. Pickup address for the courier is set in <strong>Shipping</strong>.</p>
          <div className="flex flex-wrap gap-2" data-testid="shipping-mode-toggle">
            {[
              { v: "free", l: "Always free", hint: "No shipping fee on any order" },
              { v: "fixed", l: "Fixed fee", hint: "Same fee on every order" },
              { v: "threshold", l: "Free above threshold", hint: "Fixed fee until cart total ≥ minimum" },
            ].map((opt) => {
              const active = (data.shipping_mode || "threshold") === opt.v;
              return (
                <button
                  type="button"
                  key={opt.v}
                  onClick={() => setData({ ...data, shipping_mode: opt.v })}
                  className={`flex-1 min-w-[180px] text-left px-4 py-3 rounded-xl border transition ${active ? "border-rose bg-rose/10" : "border-border hover:border-rose/50"}`}
                  data-testid={`shipping-mode-${opt.v}`}
                >
                  <p className={`text-sm font-medium ${active ? "text-rose" : "text-charcoal"}`}>{opt.l}</p>
                  <p className="text-xs text-charcoal/55 mt-0.5">{opt.hint}</p>
                </button>
              );
            })}
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            {(data.shipping_mode || "threshold") !== "free" && (
              <div data-testid="shipping-fee-input">
                <label className="eyebrow">Shipping fee (₹)</label>
                <Input type="number" min={0} value={data.shipping_fee_inr ?? 0} onChange={(e) => setData({ ...data, shipping_fee_inr: Number(e.target.value) || 0 })} />
              </div>
            )}
            {(data.shipping_mode || "threshold") === "threshold" && (
              <div data-testid="shipping-threshold-input">
                <label className="eyebrow">Free shipping above (₹)</label>
                <Input type="number" min={0} value={data.free_shipping_threshold ?? 0} onChange={(e) => setData({ ...data, free_shipping_threshold: Number(e.target.value) || 0 })} />
                <p className="text-xs text-charcoal/55 mt-1">Cart subtotal (after coupon) at or above this value gets free shipping.</p>
              </div>
            )}
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-border p-6 space-y-4 lg:col-span-2" data-testid="branding-invoice">
          <p className="eyebrow">GST invoice</p>
          <p className="text-xs text-charcoal/60 -mt-2">Used to generate the tax invoice PDF attached to every order confirmation email.</p>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="eyebrow">GSTIN <span className="normal-case tracking-normal text-charcoal/60">(15 chars; leave blank if unregistered)</span></label>
              <Input value={data.gstin || ""} onChange={set("gstin")} placeholder="27ABCDE1234F1Z5" maxLength={15} data-testid="invoice-gstin" />
            </div>
            <div>
              <label className="eyebrow">GST rate %</label>
              <Input type="number" step="0.5" min={0} max={28} value={data.gst_rate_percent ?? 3} onChange={(e) => setData({ ...data, gst_rate_percent: Number(e.target.value) || 0 })} data-testid="invoice-rate" />
              <p className="text-xs text-charcoal/55 mt-1">Default 3% for jewelry. Set 0 if unregistered.</p>
            </div>
            <div className="md:col-span-2">
              <label className="eyebrow">Registered business address</label>
              <Input value={data.brand_address || ""} onChange={set("brand_address")} placeholder="Studio 401, Hill Crest, Andheri West" data-testid="invoice-address" />
            </div>
            <div>
              <label className="eyebrow">City</label>
              <Input value={data.brand_city || ""} onChange={set("brand_city")} placeholder="Mumbai" data-testid="invoice-city" />
            </div>
            <div>
              <label className="eyebrow">State <span className="normal-case tracking-normal text-charcoal/60">(used for CGST+SGST vs IGST split)</span></label>
              <Input value={data.brand_state || ""} onChange={set("brand_state")} placeholder="Maharashtra" data-testid="invoice-state" />
            </div>
            <div>
              <label className="eyebrow">Pincode</label>
              <Input value={data.brand_pincode || ""} onChange={set("brand_pincode")} placeholder="400053" maxLength={6} data-testid="invoice-pincode" />
            </div>
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-border p-6 space-y-4">
          <p className="eyebrow">Commerce settings</p>
          <div><label className="eyebrow">Free shipping threshold</label><Input type="number" value={data.free_shipping_threshold} onChange={(e) => setData({ ...data, free_shipping_threshold: Number(e.target.value) })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="eyebrow">Currency symbol</label><Input value={data.currency_symbol} onChange={set("currency_symbol")} /></div>
            <div><label className="eyebrow">Currency code</label><Input value={data.currency_code} onChange={set("currency_code")} /></div>
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-border p-6 space-y-4 lg:col-span-2">
          <p className="eyebrow">Palette</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {["primary_color", "accent_color", "gold_color", "charcoal_color"].map(k => (
              <div key={k}>
                <label className="eyebrow block mb-1">{k.replace("_color", "").replace("_", " ")}</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={data[k]} onChange={(e) => setData({ ...data, [k]: e.target.value })} className="w-10 h-10 rounded border" />
                  <Input value={data[k]} onChange={set(k)} className="h-10" />
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="mt-6"><button className="btn-primary" onClick={save} data-testid="branding-save">Save branding</button></div>
    </div>
  );
};

export default AdminBranding;
